<template>
  <b-container fluid id="member" class="page">
    <div class="member-header">
      <h1>{{ this.page.name }}</h1>
      <p v-if="page.leave_date == null">{{ page.join_date }} - Present</p>
      <p v-else>{{ page.join_date }} - {{ page.leave_date }}</p>
    </div>
    <b-row>
      <b-col sm="12" md="4" lg="3">
        <div class="thumbnail" v-bind:style="{
          'background-image':
            'url(' + '//' + this.url + this.download_url + ')',
        }"></div>
      </b-col>
      <b-col sm="12" md="8" lg="9">
        <div class="d-flex flex-column personal-info">
          <h2>Personal Information</h2>
          <p class="richtext" v-html="this.page.bio"></p>
          <div class="additional-info">
            <div>
              <h5>Email</h5>
              <p>{{ this.page.email }}</p>
            </div>
            <div v-if="this.page.office">
              <h5>Office</h5>
              <p>{{ this.page.office }}</p>
            </div>
            <div>
              <h5>Links</h5>
              <div class="contacts-icons d-flex">
                <a v-if="this.page.authenticus_id" v-bind:href="'https://www.authenticus.pt/' + this.page.authenticus_id
                  " target="_blank">
                  <i>
                    <img src="../assets/icons/authenticus.png" alt="Authenticus Logo" />
                  </i>
                  <p>Authenticus</p>
                </a>

                <a v-if="this.page.scopus_id" v-bind:href="'https://www.scopus.com/authid/detail.uri?authorId=' +
                  this.page.scopus_id
                  " target="_blank">
                  <i>
                    <img src="../assets/icons/scopus.png" alt="Scopus Logo" />
                  </i>
                  <p>Scopus</p>
                </a>

                <a v-if="this.page.linked_in" v-bind:href="this.page.linked_in" target="_blank">
                  <i>
                    <font-awesome :icon="['fab', 'linkedin']" />
                  </i>
                  <p>LinkedIn</p>
                </a>

                <a v-if="this.page.orcid_id" v-bind:href="'https://orcid.org/' + this.page.orcid_id" target="_blank">
                  <i>
                    <img src="../assets/icons/orcid.svg" alt="Orcid Logo" />
                  </i>
                  <p>Orcid</p>
                </a>

                <a v-if="this.page.google_scholar" v-bind:href="this.page.google_scholar" target="_blank">
                  <i>
                    <img src="../assets/icons/google-scholar.svg" alt="Google Scholar Logo" />
                  </i>
                  <p>Google Scholar</p>
                </a>

                <a v-if="this.page.cv" v-bind:href="this.page.cv" target="_blank">
                  <i>
                    <img src="../assets/icons/cv.svg" alt="Curriculum Vitae Logo" />
                  </i>
                  <p>Curriculum Vitae</p>
                </a>

                <a v-if="this.page.personal_url" v-bind:href="this.page.personal_url" target="_blank">
                  <i>
                    <font-awesome icon="globe" />
                  </i>
                  <p>Website</p>
                </a>
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <div v-if="hasSupervisors" class="my-4 orientations" id="Orientations">
      <h2 @click="openSections.thesis = !openSections.thesis" class="section-title">
        <h3 class="arrow">{{ openSections.thesis ? '▼' : '▶' }}</h3>
         Thesis</h2>
      <b-collapse :visible="openSections.thesis">
        <b-row>
          <b-col v-for="(supervision, index) in sortedSupervisors" :key="index" class="supervisor-container section-column">
            <h4>{{ getSupervisionType(supervision.supervision_type) }}</h4>
            <span class="mb-1" v-if="getSupervisionDates(supervision)">{{ getSupervisionDates(supervision) }}</span>
            <br>
            <a v-if="supervision.thesis_url" :href="supervision.thesis_url" target="_blank" rel="noopener noreferrer"
              class="thesis-title-link" :title="supervision.project_title">
              <span class="thesis-title">{{ supervision.project_title }}</span>
            </a>
            <span v-else class="thesis-title" :title="supervision.project_title">{{ supervision.project_title }} </span>
            <br>
            <span v-if="supervision.supervisors.length == 1" class="supervisors-label">Supervisor: </span>
            <span v-else-if="supervision.supervisors.length > 1" class="supervisors-label">Supervisors: </span>
            <br>
            <span v-for="(supervisor, index) in supervision.supervisors" :key="index">
              <router-link :to="'/members/' + supervisor.id">
                {{ supervisor.name }}
              </router-link>
              <br>
            </span>
          </b-col>
        </b-row>
      </b-collapse>
    </div>

    <div v-if="hasOrientations" class="my-4 orientations" id="Orientations">
      <h2 @click="openSections.supervisions = !openSections.supervisions" class="section-title">
        <h3 class="arrow">{{ openSections.supervisions ? '▼' : '▶' }}</h3>
        Supervisions</h2>
      <b-collapse :visible="openSections.supervisions">
        <div v-for="group in groupedOrientations" :key="group.type" v-if="group.items.length > 0"
          class="orientation-group">
          <h4>{{ group.label }}</h4>

          <b-row>
            <b-col v-for="supervision in group.items.slice(0, shownOrientations[group.type])" :key="supervision.id"
              cols="4" md="3" lg="2">
              <supervisee :supervision_data="supervision"></supervisee>
            </b-col>
          </b-row>

          <b-button variant="primary" v-if="group.items.length > shownOrientations[group.type]"
            class="show-all-button p-0 mt-2" @click="shownOrientations[group.type] = group.items.length">Show all {{
              group.label }} students</b-button>

          <b-button variant="primary" v-else-if="group.items.length > 6" class="show-all-button p-0"
            @click="shownOrientations[group.type] = 6">Show less</b-button>
        </div>
      </b-collapse>
    </div>

    <div class="projects" v-if="related_projects.length > 0">
      <h2 @click="openSections.projects = !openSections.projects" class="section-title">
        <h3 class="arrow">{{ openSections.projects ? '▼' : '▶' }}</h3>
        Projects
      </h2>

      <b-collapse :visible="openSections.projects">
        <b-row>
          <b-col v-for="project in related_projects" :key="project.id" lg="2" md="4" sm="6">
            <project :project_data="project" :showThumbnails="false"></project>
          </b-col>
        </b-row>
      </b-collapse>
    </div>

    <div v-if="publicationsToShow.length > 0" class="publications">
      <div class="title d-flex justify-content-start align-items-center flex-wrap">
        <h2 @click="openSections.publications = !openSections.publications" class="section-title">
          <h3 class="arrow">{{ openSections.publications ? '▼' : '▶' }}</h3>
          Publications
        </h2>
        <b-form-select v-model="selected_year" :options="options_year"
          class="m-md-2 dropdown-publications-years"></b-form-select>
        <b-form-select v-model="selected_type" :options="options_type"
          class="m-md-2 dropdown-publications-years"></b-form-select>
      </div>

      <b-collapse :visible="openSections.publications">
        <h4 class="search-params">{{ selected_year }} / {{ selected_type }}</h4>
        <h5 class="results-found">
          ({{ filteredPublications.length }} results found;
          {{ publicationsToShow.length }} shown)
        </h5>

        <ul class="publications-list">
          <div v-for="(publication, idx) in publicationsToShow" v-bind:key="publication.pub_authenticus_id">

            <div v-if="idx == 0" class="year-division">
              {{ publication.year }}
            </div>
            <div v-else-if="publication.year !== publicationsToShow[idx - 1].year" class="year-division">
              {{ publication.year }}
            </div>
            <li style="margin-bottom: 1em;">
              <span class="bullet-point">
                <font-awesome icon="square" />
              </span>
              <div class="publication-info">
                <a v-if="publication.pub_authenticus_id !== 'P-XXX-XXX'" class="d-flex"
                  v-b-popover.hover.left="'See in Authenticus'" v-bind:href="'https://www.authenticus.pt/' + publication.pub_authenticus_id
                    " target="_blank">
                  <!--<p class="pub-header pub-header-title">Title:&nbsp;</p>-->
                  <p class="pub-header" style="margin-bottom: 5px; height:inherit;">
                    {{ publication.title }}
                  </p>
                </a>
                <a v-else class="d-flex" v-b-popover.hover.left="'See publication'" v-bind:href="'' + publication.link"
                  target="_blank">
                  <!--<p class="pub-header pub-header-title">Title:&nbsp;</p>-->
                  <p class="pub-header" style="margin-bottom: 5px; height:inherit;">
                    {{ publication.title }}
                  </p>
                </a>

                <div class="d-flex">
                  <div class="d-flex">
                    <!--<p class="pub-header">Authors:&nbsp;</p>-->
                    <p class="pub-authors" style="margin-bottom: 5px; font-size: 0.95em;">
                      {{ publication.authorlist }}
                    </p>
                  </div>

                  <div class="d-flex ml-3">
                    <!--<p class="pub-header">Publication Year:&nbsp;</p>-->
                    <p style="margin-bottom: 5px; font-size: 0.95em;">
                      ({{ publication.year }})
                    </p>
                  </div>
                </div>

                <div class="d-flex" v-if="
                  publication.pubtype == 'Journal Articles' &&
                  publication.volume
                ">
                  <p style="font-style: italic; margin-bottom: 5px; font-size: 0.95em;">
                    {{ publication.source }} (vol. {{ publication.volume }})
                  </p>
                </div>
                <div class="d-flex" v-else-if="publication.pubtype == 'Journal Articles'">
                  <p style="font-style: italic; margin-bottom: 5px; font-size: 0.95em;">
                    {{ publication.source }}
                  </p>
                </div>
                <div class="d-flex" v-else-if="
                  publication.pubtype == 'Conference Articles' &&
                  publication.conference
                ">
                  <p style="font-style: italic; margin-bottom: 5px; font-size: 0.95em;">
                    {{ publication.conference }}
                  </p>
                </div>
                <div class="d-flex" v-else>
                  <p style="font-style: italic; margin-bottom: 5px; font-size: 0.95em;">
                    {{ publication.source }}
                  </p>
                </div>
              </div>
            </li>
          </div>
        </ul>
        <b-button @click="showAll" variant="primary" v-if="filteredPublications.length !== publicationsToShow.length"
          class="show-all-button">Show all publications</b-button>
      </b-collapse>
    </div>
    <div v-else class="publications">
      <div class="title d-flex justify-content-start align-items-center flex-wrap">
        <h2 @click="openSections.publications = !openSections.publications" class="section-title">
          <h3 class="arrow">{{ openSections.publications ? '▼' : '▶' }}</h3>
          Publications
        </h2>
        <b-form-select v-if="openSections.publications" v-model="selected_year" :options="options_year"
          class="m-md-2 dropdown-publications-years"></b-form-select>
        <b-form-select v-if="openSections.publications" v-model="selected_type" :options="options_type"
          class="m-md-2 dropdown-publications-years"></b-form-select>
      </div>

      <b-collapse :visible="openSections.publications">
        <h4 class="search-params">{{ selected_year }} / {{ selected_type }}</h4>
        <h5 class="results-found">
          ({{ filteredPublications.length }} results found;
          {{ publicationsToShow.length }} shown)
        </h5>
        <br />
        No publications found.
      </b-collapse>

    </div>


    <div class="my-4" id="News">
      <h2 @click="openSections.news = !openSections.news" class="section-title">
        <h3 class="arrow">{{ openSections.news ? '▼' : '▶' }}</h3>
        News
      </h2>

      <b-collapse :visible="openSections.news">
        <div v-if="newsToShow.length !== 0">
          <div v-for="(article, index) in newsToShow" :key="index" id="related-news" class="my-4">
            <piece :article_data="article"></piece>
          </div>
        </div>
        <div v-else id="no-data" class="d-flex">
          <span><font-awesome icon="exclamation" fixed-width /></span>
          <p>No articles have been added yet!</p>
        </div>

        <b-button @click="loadMoreNews" variant="primary" v-if="itemsToShow < related_news.length"
          class="show-all-button">Load More News</b-button>
      </b-collapse>
    </div>

  </b-container>
</template>

<script>
import { getWagtailImage } from "@/api";
import Piece from "@/components/Article.vue";
import Project from "@/components/Project.vue";
import Member from "@/components/Member.vue";
import Supervisee from "@/components/Supervisee.vue";

export default {
  name: "Member",
  components: {
    Piece,
    Project,
    member: Member,
    supervisee: Supervisee
  },
  props: ["page"],
  created() {
      getWagtailImage(this.page.photo.id).then((response) => {
      const image = response.data;
      this.download_url = image.meta.download_url;
      this.title = image.title;
    });
    this.url = process.env.VUE_APP_API_URL;
  },
  data: function () {
    const buildFilterEntryYear = (year) => {
      return { value: year, text: year };
    };
    const filters_year = this.page.filter_years.map((year) => buildFilterEntryYear(year));
    for (let index in filters_year) {
      if (this.page.join_date != null) {
        if (parseInt(this.page.join_date.split(", ")[1]) > parseInt(filters_year[index].value)) {
          delete filters_year[index]
        }
      }
      else if (this.page.leave_date != null) {
        if (parseInt(this.page.leave_date.split(", ")[1]) < parseInt(filters_year[index].value)) {
          delete filters_year[index]
        }
      }
    }
    filters_year.sort((entry1, entry2) => entry2.value - entry1.value);
    filters_year.unshift({ value: "All Years", html: "All Years" });
    const buildFilterEntryType = (pubtype) => {
      return { value: pubtype, text: pubtype };
    }
    const filters_type = this.page.filter_types.map((pubtype) =>
      buildFilterEntryType(pubtype)
    );
    filters_type.unshift({ value: "All Types", html: "All Types" });

    let sorted_projects = [...this.page.related_projects].sort((a, b) => {
      return new Date(b.ends_at) - new Date(a.ends_at);
    })

    return {
      download_url: "",
      title: "",
      publications: this.page.get_publications,
      selected_year: "All Years",
      selected_type: "All Types",
      options_year: filters_year,
      options_type: filters_type,
      url: "",
      show_all_publications: false,
      related_projects: sorted_projects,
      related_news: this.page.related_news.reverse(),
      itemsToShow: 5,
      shownOrientations: {
        phd: 6,
        msc: 6,
        bsc: 6,
        internship: 6,
        other: 6,
      },
      openSections: {
        supervisions: true,
        projects: true,
        publications: true,
        news: true,
        thesis: true
      }
    };
  },
  computed: {
    hasSupervisors() {
      return this.page.supervisors && this.page.supervisors.length > 0
    },
    sortedSupervisors() {
      const order = {
        "internship": 4,
        "bsc": 3,
        "msc": 2,
        "phd": 1
      }

      return [...this.page.supervisors].sort((a, b) => {
        const aOrder = order[a.supervision_type] || 5
        const bOrder = order[b.supervision_type] || 5
        return aOrder - bOrder
      })
    },
    hasOrientations() {
      return this.page.supervisees && this.page.supervisees.length > 0
    },

    groupedOrientations() {
      const groups = [
        { type: "phd", label: "PhD", items: [] },
        { type: "msc", label: "MSc", items: [] },
        { type: "bsc", label: "BSc", items: [] },
        { type: "internship", label: "Internship", items: [] },
        { type: "other", label: "Other", items: [] },
      ]

      this.page.supervisees.forEach(supervision => {
        const type = supervision.supervision_type || "other"
        const group = groups.find(g => g.type === type) || groups[4]

        group.items.push(supervision)
      })

      groups.forEach(group => {
        group.items.sort((a, b) => {
          const endA = a.end_date || 9999
          const endB = b.end_date || 9999

          if (endA !== endB) {
            return endB - endA
          }

          const nameA = a.supervisee && a.supervisee.name ? a.supervisee.name : ""
          const nameB = b.supervisee && b.supervisee.name ? b.supervisee.name : ""

          return nameA.localeCompare(nameB)
        })
      })

      return groups
    },
    filteredPublications() {
      if (this.page.leave_date !== null) {
        return this.publications
          .filter(
            (publication) =>
              ("All Years" === this.selected_year ||
                publication.year === this.selected_year) &&
              ("All Types" === this.selected_type ||
                publication.pubtype === this.selected_type) &&
              publication.year >= parseInt(this.page.join_date.split(", ")[1]) &&
              publication.year <= parseInt(this.page.leave_date.split(", ")[1])
          )
          .sort((entry1, entry2) => entry2.year - entry1.year);
      }
      else {
        return this.publications
          .filter(
            (publication) =>
              ("All Years" === this.selected_year ||
                publication.year === this.selected_year) &&
              ("All Types" === this.selected_type ||
                publication.pubtype === this.selected_type) &&
              publication.year >= parseInt(this.page.join_date.split(", ")[1])
          )
          .sort((entry1, entry2) => entry2.year - entry1.year);
      }
    },
    publicationsToShow() {
      // If user clicked show all publications button, or filtered by type / year ==> is interested ==> Show all
      if (
        this.show_all_publications ||
        this.selected_year !== "All Years" ||
        this.selected_type !== "All Types"
      ) {
        return this.filteredPublications;
      } else {
        return this.filteredPublications.slice(
          0,
          Math.min(5, this.filteredPublications.length)
        );
      }
    },
    newsToShow() {
      return this.related_news.slice(0, this.itemsToShow)
    }
  },
  methods: {
    showAll() {
      this.show_all_publications = true;
    },
    getSupervisionType(type) {
      if (type === 'msc') return 'MSc'
      if (type === 'bsc') return 'BSc'
      if (type === 'phd') return 'PhD'
      if (type === 'internship') return 'Internship'

      return 'Project'
    },

    getSupervisionDates(supervision) {
      if (supervision.start_date && supervision.end_date) {
        if (supervision.start_date == supervision.end_date) {
          return `${supervision.start_date}`
        }
        else {
          return `${supervision.start_date} - ${supervision.end_date}`
        }
      }

      if (supervision.start_date) {
        return `${supervision.start_date} - Present`
      }

      return ''
    },

    log(message) {
      console.log(message)
    },
    loadMoreNews() {
      this.itemsToShow += 5;
    }
  },
};
</script>

<style lang="scss">
.search-params {
  display: inline;
  margin-right: 20px;
}

.results-found {
  display: inline;
  color: $secondary-color;
}

.publications h2 {
  color: $primary-color !important;
}

.year-division {
  color: $primary-color;
  text-align: left;
  font-size: 1.2em;
  margin-top: 1em;
  margin-bottom: 1em;
  margin-left: -40px;
  border-bottom: dashed;
  border-bottom-color: $primary-color;
  border-bottom-width: thin;
}

.section-title {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.section-column {
    flex: 0 0 20%;
    max-width: 20% !important;
}

.thesis-title {
  overflow: visible;
  text-overflow: inherit;
  white-space: wrap;
  font-style: italic;
}

#Orientations {
  .orientation-group {
    h4 {
      color: $secondary-color;
      text-transform: none !important;
    }
  }

  .supervisors-list {
    padding-left: 1.2rem;
    margin-bottom: 0;
    list-style-type: disc;

    li {
      margin-bottom: 0.25rem;
    }
  }
}

#member {
  .member-header {
    h1 {
      margin-bottom: 1.2em;
      margin-right: 0.3em;
    }

    p {
      position: absolute;
      top: 11em;
      text-transform: uppercase;
      color: #283655;
    }

    @media (max-width: 377px) {
      p {
        top: 14em;
      }
    }
  }

  .thumbnail {
    width: 100%;
    padding-bottom: 100%;
    background-size: cover;
    background-position: center;
  }

  .personal-info {
    margin-top: -0.4em;

    h2 {
      margin-bottom: 1em;

      @include media-breakpoint-down(sm) {
        margin: 1em 0 1em 0;
      }
    }

    .additional-info {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem 1rem;

      p {
        overflow-wrap: anywhere;
      }

      >div {
        flex: 0 1 250px;
        max-width: 450px;
      }

      .contacts-icons {
        a {
          display: flex;
          text-decoration: none;

          * {
            &:hover {
              cursor: pointer;
            }

            &:not(:hover)+p {
              display: none;
            }
          }

          p {
            margin-right: 0.7em;

            &:hover {
              display: block;
              font-weight: 400 !important;
            }
          }

          i * {
            font-size: 20px;
            margin-right: 0.5em;
          }

          img {
            position: relative;
            bottom: 0.12em;
            width: 20px;
            height: 20px;
          }
        }
      }
    }

    @include media-breakpoint-down(sm) {
      .additional-info {
        display: block;

        div:nth-child(5) p {
          width: 3.5em;
        }
      }
    }
  }

  .supervisor-container {
    margin-right: 2px !important;

    h5 span {
      color: $secondary-color;
      font-size: 0.9em;
      font-weight: normal !important;
    }
  }

  h4,
  h5 {
    color: $secondary-color;
    text-transform: uppercase;
  }

  .publications {
    .title {
      margin: 3em 0 1em 0;
    }

    h2 {
      margin-right: 4.2em;
    }

    @include media-breakpoint-down(sm) {
      .title {
        margin-bottom: 3em;
      }

      h2 {
        margin-bottom: 0.5em;
      }
    }
  }
}

.show-all-button {
  display: block;
  margin-top: 2em;
  margin-bottom: 2em;
  margin-left: auto !important;
  margin-right: auto !important;
}

#news {
  #related-news {
    >* {
      margin-bottom: 4em !important;
      max-height: 45vh;
    }

    :last-child {
      margin-bottom: 0;
    }

    :first-child {
      margin-top: 1em;
    }
  }

  .loading-animation {
    position: relative;
    transform: scale(0.6);
  }

  .load-more-button.bottom {
    display: block !important;
    margin-top: 8em !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .load-more-button.top {
    display: block !important;
    margin-bottom: 2em !important;
    margin-top: 2em !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }
}

.projects {
  margin-top: 2em;
}

.arrow {
  font-size: 0.8em;
  vertical-align: middle;
  position: relative;
  top: 5px;
}
</style>
